/**
 * Query parameter on the sign-in route naming where to land once an existing
 * session is admitted.
 */
export const LOGIN_REDIRECT_PARAM = 'redirect';

const AUTH_PATH_PREFIX = '/auth';
const LOGIN_PATH = '/login';

function isSignInPath(path: string): boolean {
  return (
    path === LOGIN_PATH ||
    path.startsWith(`${LOGIN_PATH}/`) ||
    path === AUTH_PATH_PREFIX ||
    path.startsWith(`${AUTH_PATH_PREFIX}/`)
  );
}

/**
 * Reads the post-login destination from a query string, returning it only when
 * it is a same-origin app path. Anything else - an absolute URL, a
 * protocol-relative `//host`, a backslash trick, a `javascript:` URI, or another
 * auth route that would bounce straight back here - yields `null`, so the
 * caller falls back to the role-based default.
 */
export function readLoginRedirect(search: string, origin: string): string | null {
  const value = new URLSearchParams(search).get(LOGIN_REDIRECT_PARAM);
  if (!value?.startsWith('/') || value.startsWith('//') || value.includes('\\')) {
    return null;
  }

  let resolved: URL;
  try {
    resolved = new URL(value, origin);
  } catch {
    return null;
  }
  if (resolved.origin !== origin) {
    return null;
  }
  // Checked on the normalised path: `/.//host` collapses its dot segment into
  // `//host`, which a hard redirect reads as protocol-relative.
  if (resolved.pathname.startsWith('//') || resolved.pathname.includes('\\')) {
    return null;
  }
  // The router decodes segments and matches them case-insensitively, so
  // `/%61uth/login` and `/AUTH/login` reach the sign-in route this page is
  // already mounted on. Compare the decoded, lower-cased path.
  let decoded: string;
  try {
    decoded = decodeURIComponent(resolved.pathname).toLowerCase();
  } catch {
    return null;
  }
  if (decoded.startsWith('//') || decoded.includes('\\') || isSignInPath(decoded)) {
    return null;
  }
  return `${resolved.pathname}${resolved.search}${resolved.hash}`;
}
